import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GruppeTier } from '../../../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/gruppe-tier';
import { GruppeTierService } from '../../../../../../projects/kiene-core/src/lib/components/gruppe-tier/gruppe-tier.service';
import { Betriebskontakt } from '../../../../../../projects/kiene-core/src/lib/components/stammdaten/betriebskontakte/betriebskontakt';
import { Haendler } from '../../../../../../projects/kiene-core/src/lib/components/stammdaten/haendler/haendler';
import { HaendlerDetailsDialogComponent } from '../../../../../../projects/kiene-core/src/lib/components/stammdaten/haendler/haendler-details-dialog/haendler-details-dialog.component';
import { API_BASE_URL_SERVICE } from '../../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { KieneBackendApiService } from '../../../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../../../projects/kiene-core/src/lib/services/message-service/message.service';

export type DashboardHerkunftHandlerEditMode = 'herkunft' | 'handler';

export interface DashboardTierlisteHerkunftSetzenDialogData {
    tierliste_id: number;
    tiere: GruppeTier[];
    gruppeId?: number;
    viehhaendler_id?: number | null;
    /** Which field to edit first when opening the dialog. */
    initialMode?: DashboardHerkunftHandlerEditMode;
}

export interface DashboardTierlisteHerkunftSetzenDialogResult {
    herkunft?: string;
    handler?: string;
}

@Component({
    selector: 'kiene-dashboard-tierliste-herkunft-setzen-dialog',
    templateUrl: './dashboard-tierliste-herkunft-setzen-dialog.component.html',
    styleUrls: ['./dashboard-tierliste-herkunft-setzen-dialog.component.scss'],
})
export class DashboardTierlisteHerkunftSetzenDialogComponent implements OnInit {
    editMode: DashboardHerkunftHandlerEditMode = 'herkunft';
    selectedKontakt: Betriebskontakt | null = null;
    removeHerkunft = false;
    removeHandler = false;
    applyToTiere = false;
    haendlerList: Haendler[] = [];
    haendlerCtrl = new FormControl<Haendler | null>(null);
    isSaving = false;

    constructor(
        private readonly dialogRef: MatDialogRef<
            DashboardTierlisteHerkunftSetzenDialogComponent,
            DashboardTierlisteHerkunftSetzenDialogResult | false
        >,
        @Inject(MAT_DIALOG_DATA) public data: DashboardTierlisteHerkunftSetzenDialogData,
        private readonly gruppeTierService: GruppeTierService,
        private readonly messageService: MessageService,
        private readonly api: KieneBackendApiService,
        private readonly dialog: MatDialog,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {
        if (data?.initialMode === 'handler' || data?.initialMode === 'herkunft') {
            this.editMode = data.initialMode;
        }
    }

    ngOnInit(): void {
        this.loadHaendler();
    }

    get isHerkunftMode(): boolean {
        return this.editMode === 'herkunft';
    }

    get anzahlTiere(): number {
        return this.data?.tiere?.length ?? 0;
    }

    get applyCheckboxLabel(): string {
        const subject = this.isHerkunftMode ? 'Herkunft' : 'Händler';
        return `${subject} auch für alle ${this.anzahlTiere} Tier(e) in der Liste setzen`;
    }

    get clearButtonLabel(): string {
        return this.isHerkunftMode ? 'Herkunft entfernen' : 'Händler entfernen';
    }

    setEditMode(mode: DashboardHerkunftHandlerEditMode): void {
        if (mode === 'herkunft' || mode === 'handler') {
            this.editMode = mode;
        }
    }

    onKontaktSelected(kontakt: Betriebskontakt): void {
        this.selectedKontakt = kontakt;
        this.removeHerkunft = false;
    }

    onKontakteSelected(kontakte: Betriebskontakt[]): void {
        if (kontakte?.length > 0) {
            this.onKontaktSelected(kontakte[0]);
        }
    }

    clearSelection(): void {
        if (this.isHerkunftMode) {
            this.selectedKontakt = null;
            this.removeHerkunft = true;
            return;
        }
        this.haendlerCtrl.setValue(null);
        this.removeHandler = true;
    }

    onHandlerSelected(): void {
        this.removeHandler = false;
    }

    haendlerComparator(haendler1: Haendler, haendler2: Haendler): boolean {
        return haendler1 && haendler2
            ? haendler1.viehhaendler_id === haendler2.viehhaendler_id
            : haendler1 === haendler2;
    }

    loadHaendler(): void {
        const params = new HttpParams().set('favorit', 1);
        this.api.getAll(this.apiBaseUrl + 'viehhaendler/read_kundenportal.php', params).subscribe({
            next: response => {
                this.haendlerList = response.records ?? [];
                this.prefillSelectedHaendler();
            },
            error: (err: HttpErrorResponse) => {
                this.messageService.errorMessage('Fehler beim Laden der Händler', err);
            },
        });
    }

    private prefillSelectedHaendler(): void {
        const id = Number(this.data?.viehhaendler_id) || 0;
        if (id <= 0) {
            return;
        }
        const match = this.haendlerList.find(h => Number(h.viehhaendler_id) === id);
        if (match) {
            this.haendlerCtrl.setValue(match);
        }
    }

    openCreateHaendlerDialog(): void {
        const createRef = this.dialog.open(HaendlerDetailsDialogComponent);
        createRef.afterClosed().subscribe((result: Haendler) => {
            if (result) {
                this.haendlerCtrl.setValue(result);
                this.removeHandler = false;
                this.loadHaendler();
            }
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    save(): void {
        if (this.isHerkunftMode) {
            this.saveHerkunft();
            return;
        }
        this.saveHandler();
    }

    private saveHerkunft(): void {
        if (!this.data?.tierliste_id) {
            this.messageService.errorMessage('Keine Tierliste ausgewählt!', null);
            return;
        }

        if (!this.selectedKontakt && !this.removeHerkunft) {
            this.messageService.errorMessage('Bitte wählen Sie einen Herkunftsbetrieb aus oder entfernen Sie ihn.', null);
            return;
        }

        if (this.applyToTiere && !this.data.tiere?.length) {
            this.messageService.errorMessage('Keine Tiere in dieser Tierliste gefunden.', null);
            return;
        }

        this.isSaving = true;
        this.gruppeTierService
            .updateTierlisteHerkunft(this.data.tierliste_id, this.removeHerkunft ? null : this.selectedKontakt, {
                applyToAnimals: this.applyToTiere,
                tiere: this.data.tiere,
            })
            .subscribe({
                next: success => {
                    this.isSaving = false;
                    if (!success) {
                        this.dialogRef.close(false);
                        return;
                    }

                    const message = this.removeHerkunft
                        ? 'Herkunftsbetrieb erfolgreich entfernt.'
                        : this.applyToTiere
                          ? 'Herkunftsbetrieb für Tierliste und alle Tiere gesetzt.'
                          : 'Herkunftsbetrieb für die Tierliste gesetzt.';
                    this.messageService.infoMessage(message);
                    const label = this.removeHerkunft
                        ? ''
                        : (this.selectedKontakt?.bezeichnung_final || this.selectedKontakt?.bezeichnung || '').trim();
                    this.dialogRef.close({ herkunft: label });
                },
                error: err => {
                    this.isSaving = false;
                    this.messageService.errorMessage('Herkunftsbetrieb konnte nicht aktualisiert werden!', err);
                },
            });
    }

    private saveHandler(): void {
        if (!this.data?.tierliste_id) {
            this.messageService.errorMessage('Keine Tierliste ausgewählt!', null);
            return;
        }

        const selectedHaendler = this.haendlerCtrl.value;
        if (!selectedHaendler?.viehhaendler_id && !this.removeHandler) {
            this.messageService.errorMessage('Bitte wählen Sie einen Händler aus oder entfernen Sie ihn.', null);
            return;
        }

        if (this.applyToTiere && !this.data.tiere?.length) {
            this.messageService.errorMessage('Keine Tiere in dieser Tierliste gefunden.', null);
            return;
        }

        this.isSaving = true;
        this.gruppeTierService
            .updateTierlisteViehhaendler(
                this.data.tierliste_id,
                this.removeHandler ? null : selectedHaendler?.viehhaendler_id ?? null,
                {
                    applyToAnimals: this.applyToTiere,
                    tiere: this.data.tiere,
                },
            )
            .subscribe({
                next: success => {
                    this.isSaving = false;
                    if (!success) {
                        this.dialogRef.close(false);
                        return;
                    }

                    const message = this.removeHandler
                        ? 'Händler erfolgreich entfernt.'
                        : this.applyToTiere
                          ? 'Händler für Tierliste und alle Tiere gesetzt.'
                          : 'Händler für die Tierliste gesetzt.';
                    this.messageService.infoMessage(message);
                    this.dialogRef.close({
                        handler: this.removeHandler ? '' : (selectedHaendler?.name || '').trim(),
                    });
                },
                error: (err: HttpErrorResponse) => {
                    this.isSaving = false;
                    this.messageService.errorMessage('Händler konnte nicht aktualisiert werden!', err);
                },
            });
    }
}
